import type { PDFDocumentProxy } from 'pdfjs-dist';
import { isBlobUrl, revokeBlobUrls } from 'shared/utils/blobUrl';
import { createLimitedMapCache } from 'shared/utils/limitedCache';
import { MAX_PREVIEW_FILE_SIZE_BYTES } from '../../../app/constants/filePreview';

import { loadPdfJs, type PdfJsModule } from './loadPdfJs';

export type PdfLoadingTask = {
  promise: Promise<PDFDocumentProxy>;
  destroy: () => Promise<void> | void;
};

export type PdfThumbnailCacheOptions = {
  cacheKey?: string;
  src?: string;
};

const PDF_RANGE_CHUNK_SIZE = 128 * 1024;
const PDF_THUMBNAIL_RANGE_CHUNK_SIZE = 64 * 1024;
const DEFAULT_CANVAS_JPEG_QUALITY = 0.85;
const THUMBNAIL_CANVAS_JPEG_QUALITY = 0.92;
const MAX_THUMBNAIL_PIXEL_RATIO = 2;
const MAX_CACHED_PDF_THUMBNAILS = 32;
const PDF_THUMBNAIL_CACHE_TTL_MS = 15 * 60 * 1000;
const MAX_CONCURRENT_PDF_THUMBNAIL_RENDERS = 1;
const MAX_CACHED_PDF_PAGE_COUNTS = 64;

export class PdfPreviewService {
  private pdfJsInitPromise: Promise<PdfJsModule> | null = null;

  private readonly pageCountBySrc = new Map<string, number>();

  private readonly pageCountPromises = new Map<
    string,
    Promise<number | undefined>
  >();

  private readonly thumbnailBlobUrls = createLimitedMapCache<string, string>({
    maxEntries: MAX_CACHED_PDF_THUMBNAILS,
    ttlMs: PDF_THUMBNAIL_CACHE_TTL_MS,
    onEvict: (_key, blobUrl) => {
      if (isBlobUrl(blobUrl)) {
        revokeBlobUrls([blobUrl]);
      }
    },
  });

  private thumbnailRenderActiveCount = 0;

  private readonly thumbnailRenderWaiters: Array<{
    run: () => void;
    reject: (reason: unknown) => void;
  }> = [];

  private thumbnailRenderGeneration = 0;

  /** Full gallery preview — range requests, no disableAutoFetch. */
  async createPreviewLoadingTask(
    src: string,
    signal: AbortSignal
  ): Promise<PdfLoadingTask> {
    return this.createLoadingTaskForSrc(src, signal, 'preview');
  }

  /** Page count via pdf.js (range requests for remote URLs). Cached per src. */
  getPageCountFromSrc(src: string): Promise<number | undefined> {
    const cachedCount = this.pageCountBySrc.get(src);
    if (cachedCount !== undefined) {
      return Promise.resolve(cachedCount);
    }

    const inFlight = this.pageCountPromises.get(src);
    if (inFlight) {
      return inFlight;
    }

    const pageCountPromise = this.loadPageCountFromSrc(src).finally(() => {
      this.pageCountPromises.delete(src);
    });
    this.pageCountPromises.set(src, pageCountPromise);
    return pageCountPromise;
  }

  private async loadPageCountFromSrc(
    src: string
  ): Promise<number | undefined> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 15000);

    try {
      const pdfDocument = await this.loadThumbnailDocument(src, controller.signal);
      const pageCount = pdfDocument.numPages;
      await pdfDocument.destroy();

      if (pageCount > 0) {
        this.rememberPageCount(src, pageCount);
        return pageCount;
      }

      return undefined;
    } catch {
      return undefined;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  private rememberPageCount(src: string, pageCount: number): void {
    if (this.pageCountBySrc.size >= MAX_CACHED_PDF_PAGE_COUNTS) {
      const oldestKey = this.pageCountBySrc.keys().next().value;
      if (oldestKey !== undefined) {
        this.pageCountBySrc.delete(oldestKey);
      }
    }

    this.pageCountBySrc.set(src, pageCount);
  }

  /** One-shot thumbnail load: range requests + disableAutoFetch. */
  async loadThumbnailDocument(
    src: string,
    signal: AbortSignal
  ): Promise<PDFDocumentProxy> {
    const loadingTask = await this.createLoadingTaskForSrc(
      src,
      signal,
      'thumbnail'
    );
    return this.loadDocumentFromLoadingTask(loadingTask);
  }

  getThumbnailBlobUrl(
    thumbnailSize: number,
    options: PdfThumbnailCacheOptions
  ): string | undefined {
    const identity = this.resolveThumbnailCacheIdentity(
      options.cacheKey,
      options.src
    );
    if (!identity) {
      return undefined;
    }

    return this.thumbnailBlobUrls.get(
      this.getThumbnailStorageKey(thumbnailSize, options)
    );
  }

  setThumbnailBlobUrl(
    thumbnailSize: number,
    options: PdfThumbnailCacheOptions,
    blobUrl: string
  ): void {
    this.thumbnailBlobUrls.set(
      this.getThumbnailStorageKey(thumbnailSize, options),
      blobUrl
    );
  }

  getRenderGeneration(): number {
    return this.thumbnailRenderGeneration;
  }

  cancelInFlightRenders(): void {
    this.thumbnailRenderGeneration += 1;
    const abortError = new DOMException('Aborted', 'AbortError');
    for (const waiter of this.thumbnailRenderWaiters) {
      waiter.reject(abortError);
    }
    this.thumbnailRenderWaiters.length = 0;
  }

  enqueueThumbnailRender<T>(
    task: () => Promise<T>,
    generation: number
  ): Promise<T> {
    if (generation !== this.thumbnailRenderGeneration) {
      return Promise.reject(new DOMException('Aborted', 'AbortError'));
    }

    return new Promise<T>((resolve, reject) => {
      const execute = (): void => {
        if (generation !== this.thumbnailRenderGeneration) {
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }

        this.thumbnailRenderActiveCount += 1;
        task()
          .then(resolve, reject)
          .finally(() => {
            this.thumbnailRenderActiveCount -= 1;
            const next = this.thumbnailRenderWaiters.shift();
            if (next) {
              next.run();
            }
          });
      };

      if (
        this.thumbnailRenderActiveCount < MAX_CONCURRENT_PDF_THUMBNAIL_RENDERS
      ) {
        execute();
        return;
      }

      this.thumbnailRenderWaiters.push({ run: execute, reject });
    });
  }

  resolveThumbnailPixelRatio(): number {
    if (typeof window === 'undefined') {
      return 1;
    }

    return Math.min(window.devicePixelRatio || 1, MAX_THUMBNAIL_PIXEL_RATIO);
  }

  resolveThumbnailRenderSize(thumbnailSize: number): number {
    return Math.round(thumbnailSize * this.resolveThumbnailPixelRatio());
  }

  computeThumbnailScale(
    viewportWidth: number,
    viewportHeight: number,
    thumbnailSize: number
  ): number {
    return thumbnailSize / Math.max(viewportWidth, viewportHeight);
  }

  canvasToBlobUrl(
    canvas: HTMLCanvasElement,
    quality = DEFAULT_CANVAS_JPEG_QUALITY
  ): Promise<string | undefined> {
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => resolve(blob ? URL.createObjectURL(blob) : undefined),
        'image/jpeg',
        quality
      );
    });
  }

  get thumbnailCanvasJpegQuality(): number {
    return THUMBNAIL_CANVAS_JPEG_QUALITY;
  }

  /** @internal exposed for tests */
  get thumbnailRangeChunkSize(): number {
    return PDF_THUMBNAIL_RANGE_CHUNK_SIZE;
  }

  getThumbnailStorageKey(
    thumbnailSize: number,
    options: PdfThumbnailCacheOptions
  ): string {
    const identity = this.resolveThumbnailCacheIdentity(
      options.cacheKey,
      options.src
    );
    return `${thumbnailSize}|${identity ?? ''}`;
  }

  private resolveThumbnailCacheIdentity(
    cacheKey?: string,
    src?: string
  ): string | undefined {
    if (cacheKey && cacheKey.length > 0) {
      return cacheKey;
    }
    if (src && src.length > 0) {
      return src;
    }
    return undefined;
  }

  /** Loaded once; every document shares the module. */
  private getPdfJs(): Promise<PdfJsModule> {
    this.pdfJsInitPromise ??= loadPdfJs();
    return this.pdfJsInitPromise;
  }

  private async validateRemoteFileSize(
    src: string,
    signal: AbortSignal
  ): Promise<boolean> {
    try {
      const response = await fetch(src, {
        method: 'HEAD',
        credentials: 'include',
        signal,
      });

      if (!response.ok) {
        return true;
      }

      const contentLength = response.headers.get('content-length');

      if (!contentLength) {
        return true;
      }

      const fileSize = parseInt(contentLength, 10);
      return fileSize <= MAX_PREVIEW_FILE_SIZE_BYTES;
    } catch {
      return true;
    }
  }

  private assertPreviewSizeWithinLimit(byteLength: number): void {
    if (byteLength === 0 || byteLength > MAX_PREVIEW_FILE_SIZE_BYTES) {
      throw new Error('PDF exceeds preview size limit');
    }
  }

  private async fetchBlobArrayBuffer(
    src: string,
    signal: AbortSignal
  ): Promise<ArrayBuffer> {
    const response = await fetch(src, { signal });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    this.assertPreviewSizeWithinLimit(arrayBuffer.byteLength);

    return arrayBuffer;
  }

  private createLoadingTaskFromArrayBuffer(
    pdfjsLib: PdfJsModule,
    arrayBuffer: ArrayBuffer,
    mode: 'preview' | 'thumbnail'
  ): PdfLoadingTask {
    return pdfjsLib.getDocument({
      data: new Uint8Array(arrayBuffer),
      disableFontFace: mode !== 'thumbnail',
      useSystemFonts: mode === 'thumbnail',
    });
  }

  private async createPreviewLoadingTaskFromUrl(
    pdfjsLib: PdfJsModule,
    src: string,
    signal: AbortSignal
  ): Promise<PdfLoadingTask> {
    const isWithinSizeLimit = await this.validateRemoteFileSize(src, signal);

    if (!isWithinSizeLimit) {
      throw new Error('PDF exceeds preview size limit');
    }

    return pdfjsLib.getDocument({
      url: src,
      withCredentials: true,
      rangeChunkSize: PDF_RANGE_CHUNK_SIZE,
      disableFontFace: true,
      useSystemFonts: false,
    });
  }

  private async createThumbnailLoadingTaskFromUrl(
    pdfjsLib: PdfJsModule,
    src: string,
    signal: AbortSignal
  ): Promise<PdfLoadingTask> {
    const isWithinSizeLimit = await this.validateRemoteFileSize(src, signal);

    if (!isWithinSizeLimit) {
      throw new Error('PDF exceeds preview size limit');
    }

    return pdfjsLib.getDocument({
      url: src,
      withCredentials: true,
      disableAutoFetch: true,
      disableStream: false,
      rangeChunkSize: PDF_THUMBNAIL_RANGE_CHUNK_SIZE,
      disableFontFace: false,
      useSystemFonts: true,
    });
  }

  private async createLoadingTaskForSrc(
    src: string,
    signal: AbortSignal,
    mode: 'preview' | 'thumbnail'
  ): Promise<PdfLoadingTask> {
    const pdfjsLib = await this.getPdfJs();

    if (isBlobUrl(src)) {
      const arrayBuffer = await this.fetchBlobArrayBuffer(src, signal);
      return this.createLoadingTaskFromArrayBuffer(pdfjsLib, arrayBuffer, mode);
    }

    if (mode === 'thumbnail') {
      return this.createThumbnailLoadingTaskFromUrl(pdfjsLib, src, signal);
    }

    return this.createPreviewLoadingTaskFromUrl(pdfjsLib, src, signal);
  }

  private async loadDocumentFromLoadingTask(
    loadingTask: PdfLoadingTask
  ): Promise<PDFDocumentProxy> {
    try {
      return await loadingTask.promise;
    } catch (error) {
      void loadingTask.destroy();
      throw error;
    }
  }
}

export const pdfPreviewService = new PdfPreviewService();
